import React from 'react';
import { useTranslation } from 'react-i18next';

import AppPaths from '../../../AppPaths';
import { BillingSubscription } from './SplashPage';
import Button from '../../../components/Button';
import ErrorMessage, { useError } from '../../../components/ErrorMessage';
import { centsToDollars, centsToDollarsNumber, getLoadingOrErrorElement } from '../../../lib/util';
import styles from '../styles/SubscriptionContainer.module.scss';
import SubscriptionAppBar from '../components/SubscriptionAppBar';
import { supplementsManagement as events } from '../../../lib/analytics/events';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import * as types from '../../../types';
import { claimSupplementDiscountDeterrentMutation, supplementsSubscriptionsQuery } from '../../../graphql-operations';
import Loading from '../../../components/Loading';
import { DateTime } from 'luxon';
import CancelDeterrentConfirmationModal from './components/CancelDeterrentConfirmationModal';
import { CancellationDeterrents } from './util/types';
import classNames from 'classnames';

interface DiscountDeterrentState {
  subscription: BillingSubscription;
}

export default function DiscountDeterrent() {
  const { t } = useTranslation();
  const pageName = '33% Off Discount Deterrent';
  const history = useHistory();
  const location = useLocation<DiscountDeterrentState>();
  const { subscription } = location.state;
  const { error, setError } = useError();

  events.pageLoad({ subscriptionId: subscription.id }, pageName);

  const [claimDiscountMutation, claimSupplementDiscountDeterrentMutationState] = useMutation<
    types.gqlTypes.ECOMMERCE_claimSupplementDiscount,
    types.gqlTypes.ECOMMERCE_claimSupplementDiscountVariables
  >(claimSupplementDiscountDeterrentMutation, {
    refetchQueries: [{ query: supplementsSubscriptionsQuery }],
    onCompleted: (data) => {
      const success = !!data.claimSupplementDiscount?.success;
      if (!success) {
        setError(
          data.claimSupplementDiscount?.reasonForFailure ??
            t('unable_to_claim_discount_please_contact_customer_support'),
        );
      } else {
        history.push({
          pathname: AppPaths.Supplements.Root,
          search: window.location.search,
          state: { toast: { success: true, change: 'discounted' }, refetch: false },
        });
      }
    },
  });

  const loading = getLoadingOrErrorElement(claimSupplementDiscountDeterrentMutationState.loading, null);

  const handleClaim = () => {
    claimDiscountMutation({
      variables: {
        input: {
          recurlySubscriptionId: subscription.id,
        },
      },
    });
  };

  const handleCancel = () => {
    events.deterrentDeclined({ subscriptionId: subscription.id }, CancellationDeterrents.DISCOUNT);
    history.push({
      pathname: AppPaths.Supplements.Survey(subscription.id),
      state: { subscription },
      search: window.location.search,
    });
  };

  return (
    <>
      <div className={classNames(styles.fullWidthWrapper, styles.updatedFullWidthWrapper)}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.goBackFromPage({ subscriptionId: subscription.id }, pageName)}
          closeButtonAnalyticsEvent={() => events.closePage({ subscriptionId: subscription.id }, pageName)}
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.largeHeader}>
            <h1>{t('how_about_33_percent_off_your_next_shipment')}</h1>
          </div>
          {loading ? (
            <Loading />
          ) : (
            <>
              <div className={styles.content}>
                <p>
                  {t(
                    'dont_lose_out_on_benefits_to_your_dogs_health_heres_a_discount_on_your_next_bag_to_keep_your_subscription_going',
                  )}
                </p>

                <div className={styles.highlight}>
                  <div className={styles.header}>{t('next_shipment')}</div>
                  <div className={styles.discountedPrice}>
                    {'$'}
                    {centsToDollars((subscription.subscriptionOption.priceInCents / 3) * 2)}{' '}
                    <span className={styles.originalPrice}>
                      {'$'}
                      {centsToDollars(subscription.subscriptionOption.priceInCents)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
          {error && <ErrorMessage errors={[error]} />}
        </div>
        <div className={classNames(styles.buttonContainer, styles.updatedButtonContainer)}>
          <CancelDeterrentConfirmationModal
            onContinue={handleClaim}
            header={t('confirm_shipment_discount')}
            body={t('your_next_shipment_on_date_will_be_discounted_33_percent_and_billed_as_amount', {
              date: DateTime.fromISO(subscription.supplementShipmentDates!.nextShipmentDate).toFormat('cccc, LLLL d'),
              amount: centsToDollarsNumber((subscription.subscriptionOption.priceInCents * 2) / 3),
            })}
            trigger={<Button className={styles.button}>{t('claim_33_percent_discount')}</Button>}
            deterrent={CancellationDeterrents.DISCOUNT}
          />
          <Button className={styles.button} onClick={() => handleCancel()} tertiary>
            {t('continue_with_cancellation')}
          </Button>
        </div>
      </div>
    </>
  );
}
